import {
  Component,
  Input,
  OnDestroy,
  ChangeDetectorRef,
  ViewChild,
  ElementRef,
  signal,
  Output,
  EventEmitter, OnInit
} from '@angular/core';
import {CoreModule} from '@abp/ng.core';
import {ModalComponent} from '@shared/components/modal/modal.component';
import Video, {
  Room,
  RemoteParticipant,
  LocalAudioTrack,
  LocalVideoTrack,
  LocalTrackPublication,
  LocalDataTrack,
  RemoteDataTrack,
} from 'twilio-video';
import {AppointmentApiService} from '@app/areas/appointments/services/appointment-api.service';
import {map, take} from 'rxjs/operators';
import ParticipantViewComponent from './participant-view/participant-view.component';
import {IconComponent} from '@shared/components/icon/icon.component';
import {ModalService} from "@services/helpers/modal/modal.service";
import {LocalizeTextPipe} from "@shared/pipes/localize-text/localize-text.pipe";
import {IntlPhoneNumComponent} from "@clinician/shared/components/intl-phone-num/intl-phone-num.component";
import {ButtonComponent} from "@shared/components/buttons/button/button.component";
import {AlertService} from "@services/helpers/alert/alert.service";
import {LocalizeService} from "@services/helpers/localize/localize.service";
import {LoaderSmallComponent} from "@shared/components/loaders/loader-small/loader-small.component";
import {AppointmentDetail} from "@app/areas/appointments/models/appointment.interface";
import {hasValue} from "@shared/utils/helpers";

@Component({
  selector: 'app-appointment-call-modal',
  imports: [
    CoreModule,
    ModalComponent,
    ParticipantViewComponent,
    IconComponent,
    LocalizeTextPipe,
    IntlPhoneNumComponent,
    ButtonComponent,
    LoaderSmallComponent
  ],
  templateUrl: './appointment-call-modal.component.html',
  styleUrls: ['./appointment-call-modal.component.scss'],
  standalone: true
})
export class AppointmentCallModalComponent implements OnDestroy, OnInit {
  @ViewChild('localPreview') localPreview!: ElementRef<HTMLDivElement>;

  @Output() onHangup = new EventEmitter<void>();

  room?: Room;
  participants: RemoteParticipant[] = [];
  private _aptId: string | null = null;

  // UI state flags
  isWaiting = true;

  // signals to drive UI state + effects
  loading$ = signal(true);
  audioEnabled$ = signal(true);
  videoEnabled$ = signal(true);
  loadingInvite$ = signal(false);
  appointment$ = signal<AppointmentDetail | null>(null)
  focused$ = signal<RemoteParticipant | null>(null);

  /**
   * Maps identityUserId: Full Name
   */
  nameMapping: { [key: string]: string } = {}

  phoneInviting$: string | null = null;
  invitingPhone$ = false;

  loggedInUserName$ = signal<string | null>(null);

  // store your local tracks here
  private localAudioTrack?: LocalAudioTrack;
  private localVideoTrack?: LocalVideoTrack;
  // Data tracks
  private localDataTrack?: LocalDataTrack;
  private remoteDataTracks: Record<string, RemoteDataTrack> = {};

  @Input() set appointmentId(id: string) {
    this._aptId = id;
    this.joinRoom();
  }

  get participantsDisplaying(): RemoteParticipant[] {
   return this.participants.filter(p => p.sid !== this.focused$()?.sid)
  }

  get appointmentId() {
    return this._aptId;
  }

  constructor(
    private modal: ModalService,
    private api: AppointmentApiService,
    private cd: ChangeDetectorRef,
    private alert: AlertService,
    private l: LocalizeService,
  ) {
  }

  ngOnInit(): void {
    this.refreshLoggedInUser();
  }

  ngOnDestroy() {
    this.leaveRoom();
  }

  refreshLoggedInUser(): void {
    this.api.getParticipant()
      .pipe(take(1))
      .subscribe({
        next: res => {
          if (hasValue(res)) {
            this.loggedInUserName$.set(`${res.firstName} ${res.lastName}`)
          }
        },
        error: err => {
        }
      })
  }

  toggleAudio() {
    if (!this.room) {
      return;
    }

    // 1) Find your LocalAudioTrackPublication
    const audioPub = Array.from(this.room.localParticipant.audioTracks.values())[0] as LocalTrackPublication;
    const audioTrack = audioPub.track as LocalAudioTrack;

    // 2) Mute/unmute via disable()/enable()
    if (audioTrack.isEnabled) {
      audioTrack.disable();
      this.audioEnabled$.set(false);
    } else {
      audioTrack.enable();
      this.audioEnabled$.set(true);
    }
    this.sendAudioEnabled(this.audioEnabled$());
    this.sendCode(this.audioEnabled$() ? 'local_audio_enabled' : 'local_audio_disabled');
  }

  toggleVideo() {
    if (!this.localVideoTrack) {
      return;
    }

    const shouldEnable = !this.videoEnabled$();
    this.localVideoTrack.enable(shouldEnable);
    this.videoEnabled$.set(shouldEnable);

    this.sendVideoEnabled(shouldEnable);
    this.sendCode(shouldEnable ? 'local_video_enabled' : 'local_video_disabled');

    // hide/show your own preview
    const vids = this.localPreview.nativeElement.querySelectorAll('video');
    vids.forEach((v: HTMLVideoElement) => v.hidden = !shouldEnable);
  }

  private sendVideoEnabled(enabled: boolean) {
    try {
      const msg = JSON.stringify({ type: 'videoEnabled', enabled });
      this.localDataTrack?.send(msg);
      console.log('[Twilio][data] sent videoEnabled=', enabled);
    } catch (e) {
      console.warn('[Twilio][data] send videoEnabled failed', e);
    }
  }

  private sendAudioEnabled(enabled: boolean) {
    try {
      const msg = JSON.stringify({ type: 'audioEnabled', enabled });
      this.localDataTrack?.send(msg);
      console.log('[Twilio][data] sent audioEnabled=', enabled);
    } catch (e) {
      console.warn('[Twilio][data] send audioEnabled failed', e);
    }
  }

  private sendCode(code: string, ctx?: Record<string, unknown>) {
    try {
      const msg = JSON.stringify({ type: 'code', code, ctx });
      this.localDataTrack?.send(msg);
      console.log('[Twilio][data] sent code=', code, 'ctx=', ctx || {});
    } catch (e) {
      console.warn('[Twilio][data] send code failed', e);
    }
  }

  private fetchParticipantNames(): void {
    this.api.get(this.appointmentId)
      .pipe(take(1))
      .subscribe({
        next: res => {
          this.appointment$.set(res)

          this.appointment$().participants.forEach(p => {
            this.nameMapping[p.userId] = `${p.firstName} ${p.lastName}`
          })
        },
        error: err => {
          this.appointment$.set(null)
        }
      })
  }

  private async joinRoom() {
    if (!this.appointmentId) {
      return;
    }

    this.loading$.set(true)

    // on join of the room, fetch names
    this.fetchParticipantNames();

    const constraints = {
      audio: true,
      video: {
        width: {ideal: 1280, max: 1920},
        height: {ideal: 720, max: 1080},
        frameRate: {ideal: 24}
      }
    };

    // 1) Create exactly one audio + one video track
    const localTracks = await Video.createLocalTracks(constraints) as [LocalAudioTrack, LocalVideoTrack];
    this.localAudioTrack = localTracks.find(t => t.kind === 'audio') as LocalAudioTrack;
    this.localVideoTrack = localTracks.find(t => t.kind === 'video') as LocalVideoTrack;

    // 1a) Create a local data track so we can send control events
    this.localDataTrack = new LocalDataTrack({ name: 'data' });
    // 2) Attach your preview once
    localTracks.forEach(t =>
      this.localPreview.nativeElement.appendChild(t.attach())
    );

    // 3) Connect with *those* tracks (no auto-tracks)
    this.room = await Video.connect(
      (await this.api.joinRoom(this.appointmentId!).pipe(take(1), map(r => r.token)).toPromise()),
      {
        name: `appointment-${this.appointmentId}`,
        tracks: [...localTracks, this.localDataTrack].filter(Boolean) as (LocalAudioTrack | LocalVideoTrack | LocalDataTrack)[]
      }
    );

    // Broadcast local presence + initial AV state so other clients get context
    try {
      const me = this.room.localParticipant?.identity;
      if (me) {
        this.sendCode('participant_joined', { id: me });
      }
      // initial AV state
      this.sendVideoEnabled(this.videoEnabled$());
      this.sendAudioEnabled(this.audioEnabled$());
      this.sendCode(this.videoEnabled$() ? 'local_video_enabled' : 'local_video_disabled');
      this.sendCode(this.audioEnabled$() ? 'local_audio_enabled' : 'local_audio_disabled');
    } catch (e) {
      console.warn('[Twilio][data] failed to send initial presence/AV state', e);
    }

    this.loading$.set(false)

    // 3) initial state
    this.isWaiting = this.room.participants.size === 0;
    this.cd.detectChanges();

    // 4) Wire up participants
    this.room.participants.forEach(p => this.onParticipant(p));
    this.room.on('participantConnected', p => this.onParticipant(p));
    this.room.on('participantDisconnected', p => this.onDisconnect(p));
    this.room.once('disconnected', r => {
      r.localParticipant.tracks.forEach(pub => {
        pub.track?.stop();
        pub.track?.detach().forEach(el => el.remove());
      });
      this.sendCode('left_room');
      this.modal.close()
    });
  }

  private onParticipant(p: RemoteParticipant) {
    console.log('onParticipant')
    // first participant is joining
    if (this.participants.length === 0) {
      this.isWaiting = false;
      this.cd.detectChanges();
    }

    this.participants = [...this.participants, p];
    if (!hasValue(this.focused$())) {
      this.select(p.sid)
    }

    // Inform peers about the new participant (id = Twilio identity)
    try {
      this.sendCode('participant_joined', { id: p.identity });
    } catch {}

    this.cd.detectChanges();

    /**
     * Re-fetching names
     *
     * This is handling an edge case where the user updates their name before joining the appointment, which
     * means that the participant information previously build with the identity name maps will be out of date. An
     * alternate solution to this problem might be using the data tracks to fire some events for when the user updates
     * their details, but this is out of scope for the MVP for now. 02/07/2025
     */
    this.fetchParticipantNames()
  }

  private onDisconnect(p: RemoteParticipant) {
    // Inform peers the participant left
    try {
      this.sendCode('participant_left', { id: p.identity });
    } catch {}
    // Clean up remote data tracks before participant removal
    delete this.remoteDataTracks[p.identity];
    console.log('onDisconnect')
    // Remove participant from the list
    this.participants = this.participants.filter(x => x.sid !== p.sid);
    // If the focused participant was removed, update focus
    if (this.focused$() && this.focused$().sid === p.sid) {
      this.focused$.set(this.participants[0] || null);
    }
    if (this.participants.length === 0) {
      this.isWaiting = true;
    }
    this.cd.detectChanges();
  }

  select(sid: string) {
    this.focused$.set(this.participants.find(p => p.sid === sid)!)
    this.cd.detectChanges()
  }

  leaveRoom() {
    if (this.loading$()) {
      return;
    }

    // 1) best-effort notify peers then close the modal
    try {
      const me = this.room?.localParticipant?.identity;
      if (me) {
        this.sendCode('participant_left', { id: me });
      }
      this.sendCode('left_room');
    } catch {}
    this.modal.close();

    // 2) disconnect the room if joined
    if (this.room) {
      this.room.disconnect();
      this.room = undefined;
    }

    // 3) stop & detach your local audio track
    if (this.localAudioTrack) {
      this.localAudioTrack.stop();
      this.localAudioTrack.detach().forEach(el => el.remove());
      this.localAudioTrack = undefined;
    }

    // 4) stop & detach your local video track
    if (this.localVideoTrack) {
      this.localVideoTrack.stop();
      this.localVideoTrack.detach().forEach(el => el.remove());
      this.localVideoTrack = undefined;
    }
    // 4b) stop local data track
    this.localDataTrack = undefined;

    // 5) reset participants & focus
    this.participants = [];
    this.focused$.set(null)
    this.remoteDataTracks = {};

    // 6) notify parent
    this.onHangup.emit();
  }

  inviteViaPhone(): void {
    this.loadingInvite$.set(true)
    this.api.inviteViaPhone(this.appointmentId, this.phoneInviting$)
      .pipe(take(1))
      .subscribe({
        next: res => {
          this.loadingInvite$.set(false)
          this.invitingPhone$ = false;
          this.alert.success(this.l.l("AppointmentInviteSent.Success", "Your invitation to join this appointment has been sent successfully"))
        },
        error: err => {
          console.error('error inviting user')
          console.error(err)
          this.loadingInvite$.set(false)
          this.alert.info(this.l.l("AppointmentInviteSent.Failed", "Failed to send your invitation, please try again."))
        }
      })
  }
}
